import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api, type EscalationWithDetails } from '../lib/api';
import { useNavigate } from 'react-router-dom';

export const NotificationBell: React.FC = () => {
  const [showDropdown, setShowDropdown] = useState(false);
  const navigate = useNavigate();

  // Poll for pending escalations every 30 seconds
  const { data } = useQuery({
    queryKey: ['pendingEscalations'],
    queryFn: async () => {
      const response = await api.getEscalations({
        status: 'pending',
        limit: 10,
      });
      return response.escalations;
    },
    refetchInterval: 30000, // Poll every 30 seconds
  });

  const pendingCount = data?.length || 0;

  const handleNotificationClick = (_escalation: EscalationWithDetails) => {
    setShowDropdown(false);
    navigate('/expert-dashboard');
  };

  const formatTimeAgo = (date: string): string => {
    const seconds = Math.floor((new Date().getTime() - new Date(date).getTime()) / 1000);
    
    if (seconds < 60) return 'just now';
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
    return `${Math.floor(seconds / 86400)}d ago`;
  };

  return (
    <div className="relative">
      <button
        onClick={() => setShowDropdown(!showDropdown)}
        className="relative p-2 text-gray-600 hover:text-gray-900 focus:outline-none"
      >
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
          />
        </svg>
        {pendingCount > 0 && (
          <span className="absolute top-0 right-0 inline-flex items-center justify-center px-2 py-1 text-xs font-bold leading-none text-white transform translate-x-1/2 -translate-y-1/2 bg-red-600 rounded-full">
            {pendingCount > 9 ? '9+' : pendingCount}
          </span>
        )}
      </button>

      {showDropdown && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-10"
            onClick={() => setShowDropdown(false)}
          />

          {/* Dropdown */}
          <div className="absolute right-0 mt-2 w-80 bg-white rounded-lg shadow-lg border border-gray-200 z-20 max-h-96 overflow-y-auto">
            <div className="p-4 border-b border-gray-200">
              <h3 className="text-sm font-semibold text-gray-900">
                Expert Notifications
              </h3>
              <p className="text-xs text-gray-500 mt-1">
                {pendingCount === 0
                  ? 'No pending escalations'
                  : `${pendingCount} pending escalation${pendingCount > 1 ? 's' : ''}`}
              </p>
            </div>

            <div className="divide-y divide-gray-200">
              {!data || data.length === 0 ? (
                <div className="p-4 text-center text-sm text-gray-500">
                  No new notifications
                </div>
              ) : (
                data.map((escalation) => (
                  <button
                    key={escalation.id}
                    onClick={() => handleNotificationClick(escalation)}
                    className="w-full p-4 text-left hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <p className="text-sm font-medium text-gray-900">
                          {escalation.user.displayName}
                        </p>
                        <p className="text-xs text-gray-500 mt-1 line-clamp-2">
                          {escalation.conversation.title}
                        </p>
                        <div className="flex items-center gap-2 mt-2">
                          <span
                            className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                              escalation.priority === 'urgent'
                                ? 'bg-red-100 text-red-800'
                                : escalation.priority === 'high'
                                ? 'bg-orange-100 text-orange-800'
                                : 'bg-blue-100 text-blue-800'
                            }`}
                          >
                            {escalation.priority}
                          </span>
                          <span className="text-xs text-gray-400">
                            {formatTimeAgo(escalation.escalatedAt)}
                          </span>
                        </div>
                      </div>
                      <svg
                        className="w-5 h-5 text-gray-400 flex-shrink-0 ml-2"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 5l7 7-7 7"
                        />
                      </svg>
                    </div>
                  </button>
                ))
              )}
            </div>

            {pendingCount > 0 && (
              <div className="p-4 border-t border-gray-200">
                <button
                  onClick={() => {
                    setShowDropdown(false);
                    navigate('/expert-dashboard');
                  }}
                  className="w-full px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 text-sm font-medium"
                >
                  View All Escalations
                </button>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};
